import { REPORT_TEMPLATE_LIMITS, type ReportTemplate, type ReportTemplateSection } from '@hoopoe/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Archive, ArchiveRestore, ArrowDown, ArrowUp, LayoutTemplate, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useReauth } from '../components/auth/reauth';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { EditorError, Panel, TextRow } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { ApiError, api } from '../lib/api';
import { templatesKey, useReportTemplates } from '../lib/reports';

// Report templates (PLAN §10, §14 task 31): admin-defined section structures a report starts from. The seeded
// "Synthesis note" follows the program's synthesis notes. Each section has a title, guidance for whoever writes it,
// and optionally a starter question (kept as it is when an admin edits the text). Templates are archived, not
// deleted: reports keep a copy of the sections they started with.

export const Route = createFileRoute('/admin/report-templates')({ component: ReportTemplatesPage });

type Draft = { name: string; description: string; sections: ReportTemplateSection[] };

function useTemplateWrite() {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: ({ path, method = 'POST', body }: { path: string; method?: string; body: unknown }) =>
      withReauth(() => api<ReportTemplate>(path, { method, body })),
    onSuccess: () => {
      setError(null);
      return qc.invalidateQueries({ queryKey: templatesKey });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setError(e instanceof ApiError ? e.message : 'Something went wrong. Try again.');
    },
  });
  return { ...m, error };
}

function ReportTemplatesPage() {
  const templates = useReportTemplates(true);
  const [editing, setEditing] = useState<string | null>(null);
  const write = useTemplateWrite();
  const list = templates.data?.templates ?? [];
  return (
    <>
      <PageHeader
        title="Report templates"
        description="Section structures a report starts from: headings, guidance for the writer, and a starter question per section."
        actions={
          editing !== 'new' && (
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus aria-hidden="true" /> New template
            </Button>
          )
        }
      />
      <Tip id="admin-report-templates" learnMore="templates-for-reports">
        Reports keep a copy of the sections they started with, so changing a template never rewrites a report someone already made.
      </Tip>
      <div className="mt-4 space-y-4">
        <EditorError error={write.error} />
        {editing === 'new' && (
          <TemplateEditor
            initial={{ name: '', description: '', sections: [{ key: '', title: '', guidance: '' }] }}
            busy={write.isPending}
            onCancel={() => setEditing(null)}
            onSave={d => write.mutate({ path: '/admin/report-templates', body: d }, { onSuccess: () => setEditing(null) })}
          />
        )}
        {templates.isError ? (
          <ErrorState error={templates.error} title="Templates couldn’t be loaded" onRetry={() => templates.refetch()} compact />
        ) : templates.isPending ? (
          <div className="h-24 animate-pulse rounded-md bg-muted" aria-busy="true">
            <span className="sr-only">Loading…</span>
          </div>
        ) : list.length === 0 ? (
          <EmptyState icon={LayoutTemplate} title="No report templates" learnMore="templates-for-reports">
            Add one to give reports a structure.
          </EmptyState>
        ) : (
          list.map(t =>
            editing === t.id ? (
              <TemplateEditor
                key={t.id}
                initial={{ name: t.name, description: t.description ?? '', sections: t.sections }}
                busy={write.isPending}
                onCancel={() => setEditing(null)}
                onSave={d =>
                  write.mutate({ path: `/admin/report-templates/${t.id}`, method: 'PATCH', body: d }, { onSuccess: () => setEditing(null) })
                }
              />
            ) : (
              <Panel
                key={t.id}
                title={
                  <span className="flex items-center gap-2">
                    {t.name}
                    {t.archived && <span className="rounded-full border px-1.5 text-xs font-normal text-muted-foreground">Archived</span>}
                  </span>
                }
                actions={
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(t.id)} aria-label={`Edit ${t.name}`}>
                      <Pencil aria-hidden="true" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`${t.archived ? 'Restore' : 'Archive'} ${t.name}`}
                      onClick={() =>
                        write.mutate({ path: `/admin/report-templates/${t.id}`, method: 'PATCH', body: { archived: !t.archived } })
                      }
                    >
                      {t.archived ? <ArchiveRestore aria-hidden="true" /> : <Archive aria-hidden="true" />}{' '}
                      {t.archived ? 'Restore' : 'Archive'}
                    </Button>
                  </div>
                }
              >
                {t.description && <p className="text-sm text-muted-foreground">{t.description}</p>}
                <ol className="list-decimal space-y-1 ps-5 text-sm">
                  {t.sections.map(s => (
                    <li key={s.key}>
                      <span className="font-medium">{s.title}</span>
                      {s.starter && <span className="ms-2 text-xs text-muted-foreground">· starter question</span>}
                      {s.guidance && <p className="text-xs text-muted-foreground">{s.guidance}</p>}
                    </li>
                  ))}
                </ol>
              </Panel>
            ),
          )
        )}
      </div>
    </>
  );
}

function TemplateEditor({
  initial,
  busy,
  onSave,
  onCancel,
}: {
  initial: Draft;
  busy: boolean;
  onSave: (d: Draft) => void;
  onCancel: () => void;
}) {
  const [d, setD] = useState<Draft>(initial);
  const setSection = (i: number, patch: Partial<ReportTemplateSection>) =>
    setD(x => ({ ...x, sections: x.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const move = (i: number, by: -1 | 1) =>
    setD(x => {
      const s = [...x.sections];
      const [item] = s.splice(i, 1);
      if (item) s.splice(i + by, 0, item);
      return { ...x, sections: s };
    });
  return (
    <Panel title={initial.name ? `Edit “${initial.name}”` : 'New template'}>
      <form
        className="space-y-4"
        aria-label={initial.name ? `Edit ${initial.name}` : 'New report template'}
        onSubmit={e => {
          e.preventDefault();
          // Sections without a key get one from their title on the server.
          onSave({ ...d, sections: d.sections.map(s => (s.key ? s : { title: s.title, guidance: s.guidance, key: '' })) });
        }}
      >
        <TextRow label="Name" value={d.name} required onChange={name => setD(x => ({ ...x, name }))} placeholder="e.g. Case brief" />
        <TextRow label="Description" value={d.description} multiline onChange={description => setD(x => ({ ...x, description }))} />
        <ol className="space-y-3">
          {d.sections.map((s, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: new sections have no key until saved
            <li key={i} className="space-y-2 rounded-md border bg-background p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">Section {i + 1}</span>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    aria-label={`Move section ${i + 1} up`}
                  >
                    <ArrowUp aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    disabled={i === d.sections.length - 1}
                    onClick={() => move(i, 1)}
                    aria-label={`Move section ${i + 1} down`}
                  >
                    <ArrowDown aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    disabled={d.sections.length === 1}
                    onClick={() => setD(x => ({ ...x, sections: x.sections.filter((_, j) => j !== i) }))}
                    aria-label={`Remove section ${i + 1}`}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                </div>
              </div>
              <TextRow label={`Section ${i + 1} title`} value={s.title} required onChange={title => setSection(i, { title })} />
              <TextRow
                label={`Section ${i + 1} guidance`}
                value={s.guidance}
                multiline
                help="What the writer should cover in this section. Shown next to the report."
                onChange={guidance => setSection(i, { guidance })}
              />
              {s.starter && <p className="text-xs text-muted-foreground">Has a starter question (kept as it is).</p>}
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap gap-2">
          {d.sections.length < REPORT_TEMPLATE_LIMITS.sections && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setD(x => ({ ...x, sections: [...x.sections, { key: '', title: '', guidance: '' }] }))}
            >
              <Plus aria-hidden="true" /> Add section
            </Button>
          )}
          <span className="flex-1" />
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={busy}>
            Save template
          </Button>
        </div>
      </form>
    </Panel>
  );
}
